import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

import { DependenciesRepository, DependencyManagementRepository } from '@data-layer/repositories';
import { AlertService } from './alert.service';
import { ManagementCompanyModel, ManagementEntityEventModel, ManagementUserModel } from '@data-layer/models/management';
import { UserModel } from '@data-layer/models/user';
import { CompanyModel } from '@data-layer/models/companies';
import { DependencyModel, DependencyRegistrableEntityModel } from '@data-layer/models/dependencies';
import { enumDependencyModelType, enumDependencyState } from '@data-layer/enums/dependencies';
import { enumAddressType, enumIdentifierQualifier, enumRespErrors } from '@data-layer/enums/common';
import { EntityModel } from '@data-layer/models/entities';
import { IsfV1EntityModel } from '@data-layer/models/isf-v1';
import { DateTimezoneService } from './date-timezone.service';
import { cloneDeep, findLast, isEqual } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class DependenciesService {
  constructor(
    private dependenciesRepository: DependenciesRepository,
    private alertService: AlertService,
    private dateTimezoneService: DateTimezoneService,
    private dependencyManagementRepository: DependencyManagementRepository,
  ) {}

  public getCompanyDependencies(companyId: number, isManagement?: boolean): Observable<DependencyModel[]> {
    return isManagement
      ? this.dependencyManagementRepository.getDependenciesForCompany(companyId)
      : this.dependenciesRepository.getDependenciesForCompany(companyId);
  }

  public getDependencyEvents(dependencyId: number): Observable<ManagementEntityEventModel[]> {
    return this.dependenciesRepository.getDependencyEvents(dependencyId).pipe(map((events) => events.filter((item) => !!item.message)));
  }

  public updateRegisteredCompany(
    company: CompanyModel | ManagementCompanyModel,
    user: UserModel | ManagementUserModel,
    isManagement?: boolean,
  ): Observable<null> {
    return this.getCompanyDependencies(company.id, isManagement).pipe(
      switchMap((dependencies) => {
        if (!dependencies || dependencies.length === 0) {
          return isManagement ? this.registerManagementCompany(company, user) : this.registerCompany(company, user);
        }

        const depModel = this.getCurrentEntityRegistrable(company, user);

        const matchedDependency = findLast(dependencies, (item: DependencyModel) => {
          const depValue = item.value;
          if (depValue.dateOfBirth) {
            depValue.dateOfBirth = this.dateTimezoneService.startOfTheDay(depValue.dateOfBirth);
          }

          return item.dependantId === company.id && isEqual(depModel, depValue);
        });

        if (!matchedDependency || matchedDependency.state === enumDependencyState.REJECTED) {
          return isManagement ? this.registerManagementCompany(company, user) : this.registerCompany(company, user);
        }

        return of(null);
      }),
    );
  }

  private shouldSkipImporterRegistration(company: CompanyModel | ManagementCompanyModel, user: UserModel | ManagementUserModel): boolean {
    const requiresDateOfBirth: boolean = company?.identifierQualifier === enumIdentifierQualifier.SSN;
    return !user.phoneNumber || (requiresDateOfBirth && !company.dateOfBirth);
  }

  public registerCompany(company: CompanyModel | ManagementCompanyModel, user: UserModel | ManagementUserModel) {
    // Registration is now handled by backend automatically - skip API call
    return of(null);
  }

  public registerManagementCompany(company: CompanyModel | ManagementCompanyModel, user: UserModel | ManagementUserModel) {
    const invalid = this.shouldSkipImporterRegistration(company, user);
    return invalid
      ? of(null)
      : this.dependencyManagementRepository.registerImporterForCompany(company.id, this.getCurrentEntityRegistrable(company, user)).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status >= 400 && error.status < 500) {
              this.alertService.error(
                error.error && error.error.tag === enumRespErrors.ImporterRegistrationStartFailed
                  ? 'KNOWN_ERRORS.IMPORTER_REGISTRATION_START_FAILED'
                  : 'LABELS.SOMETHING_DONT_WORK_MESSAGE',
              );
            }

            throw error;
          }),
        );
  }

  public getCurrentEntityRegistrable(
    company: CompanyModel | ManagementCompanyModel,
    user: UserModel | ManagementUserModel,
  ): DependencyRegistrableEntityModel {
    const { businessType, address, businessName, identifier, identifierQualifier } = company;
    let { dateOfBirth } = company;
    const { email, phoneNumber } = user;
    if (dateOfBirth) {
      dateOfBirth = this.dateTimezoneService.startOfTheDay(dateOfBirth);
    }
    return {
      businessType,
      addressType: enumAddressType.RESIDENCE,
      name: businessName,
      entityIdentifier: identifier,
      entityIdentifierQualifier: identifierQualifier,
      email,
      phone: phoneNumber,
      dateOfBirth: dateOfBirth as any,
      address,
      fax: null,
    };
  }

  public static findImporterDep(items: DependencyModel[], entity: EntityModel | IsfV1EntityModel) {
    return DependenciesService.findDep(
      items,
      entity,
      enumDependencyModelType.IMPORTER,
      entity['entityIdentifier'] ? 'entityIdentifier' : 'identifier',
      'entityIdentifier',
    );
  }

  public static findConsigneeDep(items: DependencyModel[], entity: EntityModel | IsfV1EntityModel) {
    return DependenciesService.findDep(
      items,
      entity,
      [enumDependencyModelType.CONSIGNEE, enumDependencyModelType.IMPORTER],
      entity['entityIdentifier'] ? 'entityIdentifier' : 'identifier',
      'entityIdentifier',
    );
  }

  public static findManufacturerDep(items: DependencyModel[], entity: EntityModel | IsfV1EntityModel) {
    return DependenciesService.findDep(items, entity, enumDependencyModelType.MANUFACTURER, 'name', 'name');
  }

  private static findDep(
    items: DependencyModel[],
    entity: EntityModel | IsfV1EntityModel,
    code: enumDependencyModelType | enumDependencyModelType[],
    entityField: keyof EntityModel | keyof IsfV1EntityModel,
    depField: keyof DependencyRegistrableEntityModel,
  ) {
    const codeArray: enumDependencyModelType[] = Array.isArray(code) ? cloneDeep(code) : [code];

    return findLast(items, (item) => codeArray.includes(item.type) && item.value[depField] === entity[entityField]);
  }
}
